import * as React from 'react';
import { toast } from 'react-toastify';
import Invoices from './Invoices';
import type { InvoiceDetails, InvoiceItems } from './models/InvoiceInterfaces';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { GetQuery, PostQuery } from './Shared';

const SendInvoicesData = () => {
    let navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("Waiting");
    const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetails[]>([],);
    const [invoiceTotalFormatted, setInvoiceTotalFormatted] = useState("$0.00");
    const [canEdit, setCanEdit] = useState(false);
    const [canEditHirePeriod, setCanEditHirePeriod] = useState(false);
    const [canView, setCanView] = useState(true);
    const [canSend, setCanSend] = useState(false);
    const [page, setPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageSize, setPageSize] = useState(30);
    const [batchSending, setBatchSending] = useState(false);

    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        const apiUrl = "CheckInvoicingPermission";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            if (result.data.canView == true) {
                setCanEdit(result.data.canEdit);
                setCanEditHirePeriod(result.data.canEditHirePeriod);
                setCanSend(result.data.canSend);
                //enter the page that quickbooks needs to return to, id blank as not used via invoicing
                var page = {
                    page: "sendinvoices",
                    id: 0
                };
                //encode to base 64 so can retrieve it later
                var encoded = window.btoa(JSON.stringify(page));

                checkReckonAuthorised(encoded);
            } else {
                setCanView(result.data.canView);
                //redirect to 403 permission denied
                navigate("/accessdenied")
            }
        };
    }

    const checkReckonAuthorised = async (encodedPage: string) => {
        const apiUrl = "ReckonInvoiceAuthoriseCheck?returnUrl=" + encodedPage;
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            //continue if Reckon user is authorised or if Reckon is switched off
            if (result.data.isAuthorised || !result.data.reckonSwitchedOn) {
                getInvoices(tab, 1, true);
            } else {
                window.location.replace(result.data.reckonLoginUrl)
            }
        };
    }

    const getInvoices = async (invoiceType: string, page: number, getTotalRecords: boolean) => {
        var totalInvRecords = getTotalRecords ? 0 : totalRecords;

        if (invoiceType == "Waiting") {
            const apiUrl = "GetAwaitingConfirmationInvoices?page=" + page + '&totalRecords=' + totalInvRecords + '&getTotalRecords=' + getTotalRecords;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var invoices = result.data.invoices;
                for (var i = 0; i < invoices.length; i++) {
                    var invoice = invoices[i];

                    for (var y = 0; y < invoice.items.length; y++) {
                        invoice.items[y].itemIndex = y;
                        invoice.items[y].amount = invoice.items[y].amount.toFixed(2);
                    }
                    //add blank row if canEdit invoice and user can edit
                    if (invoice.canEdit && canEdit) {
                        var newRow: InvoiceItems = {
                            itemIndex: invoice.items.length,
                            id: 0,
                            jobInvoiceId: invoice.id,
                            branchIdent: "",
                            item: "",
                            itemId: -1,
                            itemTypeId: -1,
                            invItemType: "",
                            invItemTypeId: -1,
                            quantityType: "",
                            purchaseOrderNumber: "",
                            additionalHireRate: "0.00",
                            rate: "0.00",
                            rateFormatted: "$0.00",
                            gst: "0.00",
                            gstFormatted: "$0.00",
                            chargeGst: true,
                            amount: "",
                            amountFormatted: "$0.00",
                            description: "",
                            status: 1,
                            invoiceStartDate: "",
                            invoiceEndDate: "",
                            invoiceStartDateFormatted: "",
                            invoiceEndDateFormatted: "",
                            hirePeriodDays: "",
                            hirePeriod: "",
                            dayRate: "",
                            weekMonthRate: "",
                            isDirty: false
                        }
                        invoice.items.push(newRow);
                    }
                }

                setTab(invoiceType);
                setInvoiceDetails(invoices);
                setInvoiceTotalFormatted(result.data.invoiceTotalFormatted);
                setPage(page);
                setTotalRecords(result.data.totalRecords);
                setPageSize(result.data.pageSize);
                setBatchSending(result.data.batchBeingSentToReckon);
                setLoading(false);
            };
        } else if (invoiceType == "Error") {
            const apiUrl = "GetSentErrorInvoices";
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var errorInvoices = result.data.invoices;
                for (var i = 0; i < errorInvoices.length; i++) {
                    var invoice = errorInvoices[i];
                    invoice.errors = {};

                    for (var y = 0; y < invoice.items.length; y++) {
                        invoice.items[y].itemIndex = y;
                    }
                }

                setTab(invoiceType);
                setInvoiceDetails(errorInvoices);
                setInvoiceTotalFormatted(result.data.invoiceTotalFormatted);
                setPage(page);
                setTotalRecords(result.data.totalRecords);
                setPageSize(result.data.pageSize);
                setLoading(false);
            };
        }
    }

    const tabSelected = (tab: string) => {
        getInvoices(tab, 1, true);
    }

    const tabChanged = (e: any) => {
        tabSelected(e.target.value);
    }

    const noChange = (_e: any) => {
        //no changes as user doesn't actually click on the radio button
    }

    const submit = (updatedInvoices: InvoiceDetails[]) => {
        if (updatedInvoices.length > 0) {
            saveInvoice(updatedInvoices);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    const saveInvoice = async (invoices: InvoiceDetails[]) => {
        var invoiceitems = [];
        for (var inv = 0; inv < invoices.length; inv++) {
            var item = {
                jobId: invoices[inv].jobId,
                customerId: invoices[inv].customerId,
                invoiceItems: invoices[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var invDetails = {
            invoiceDetails: invoiceitems
        }

        //save
        var jsonItem = JSON.stringify(invDetails);

        const apiUrl = "SaveInvoiceItems";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            getInvoices("Waiting", 1, false);
            setPage(1);
            toast.success("Invoices Saved");
        };
    }

    const saveHirePeriod = (updatedInvoices: InvoiceDetails[]) => {
        if (updatedInvoices.length > 0) {
            saveInvoiceHirePeriod(updatedInvoices);
        } else {
            toast.info("No invoices have been changed");
        }
    }

    const saveInvoiceHirePeriod = async (invoices: InvoiceDetails[]) => {
        var invoiceitems = [];
        for (var inv = 0; inv < invoices.length; inv++) {
            var item = {
                jobId: invoices[inv].jobId,
                invoiceItems: invoices[inv].items.filter(i => i.isDirty)
            }
            invoiceitems.push(item);
        }

        var invDetails = {
            invoiceDetails: invoiceitems
        }

        var jsonItem = JSON.stringify(invDetails);

        const apiUrl = "SaveInvoiceItemHirePeriod";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            getInvoices(tab, page, false);
            toast.success("Invoices Saved");
        };
    }

    const updateInvoiceDetails = (updatedInvoices: InvoiceDetails[]) => {
        setInvoiceDetails(updatedInvoices);
    }

    const pageChanged = (updatedPage: number) => {
        setPage(updatedPage);
        getInvoices(tab, updatedPage, false);
    }

    var invoiceList = <Invoices invoiceDetails={invoiceDetails} invoiceTotalFormatted={invoiceTotalFormatted} showCustType={true} showJobInfo={true} showQuoteNo={false} showHirePeriod={tab == "Waiting" ? true : false} showStatus={false} showStartDate={false} showPaidDate={false} showPaid={false} showSend={true} showRemove={false} showSentTo={false} showReckonInvNo={true} showError={tab == "Waiting" ? false : true} showSent={tab == "Waiting" ? false : true} canEdit={tab == "Waiting" ? canEdit : false} canEditHirePeriod={tab == "Waiting" ? canEditHirePeriod : false} canSend={canSend} canUpdatePaid={false} page={page} pageSize={pageSize} totalRecords={totalRecords} pageChanged={pageChanged} beforeValid={updateInvoiceDetails} afterValid={submit} saveHirePeriod={saveHirePeriod} />

    const renderDetails = (
        <div>
            <div className={canView ? "hidden" : ""}>
                <h2 className="error">You do not have permission to view this page!</h2>
            </div>
            <div className={canView ? "tabsComponent" : "hidden"} id="tab-container">
                <div className={batchSending ? "newJobPricelist--prepaid" : "hidden"}>A batch is being sent to Reckon. Wait 5 mins and refresh the screen.</div>
                <div className="tabsComponent__showDropdown">
                    <label className="input-group" htmlFor="scheduleTab">
                        <span className="label">Invoices</span>
                        <select className="select" id="scheduleTab" value={tab} onChange={tabChanged} disabled={batchSending} >
                            <option value="Waiting">Waiting</option>
                            <option value="Error">Error</option>
                        </select>
                    </label>
                </div>

                <div className={batchSending ? "hidden" : "tabsComponent__tabs tabsComponent__hideTabs"}>
                    <input type="radio" name="tabs" id="tab1" className="tabsComponent__control" checked={tab == 'Waiting'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Waiting')}>Waiting</label>
                    <input type="radio" name="tabs" id="tab2" className="tabsComponent__control" checked={tab == 'Error'} onChange={(e) => noChange(e)}></input>
                    <label className="tabsComponent__label" htmlFor="tabItem" onClick={() => tabSelected('Error')}>Error</label>
                </div>

                <div className={batchSending ? "hidden" : "tabsComponent__content tabsComponent__content--selected"}>
                    {invoiceList}
                </div>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
};

export default SendInvoicesData;